import { createAppSlice } from "../app/createAppSlice";
import { createSelector } from "@reduxjs/toolkit";
import { AppDispatch, IRootState } from "../app/store";
import {
  ISpaceValidationRun,
  thunkRunSpaceValidations,
  thunkToast,
} from "../DesignTab/designSlice";
import { getMcbimSpacesFromFreeTextSearch, PartialMcBimSpace } from "../utils";
import {
  GetMcbimSpacesApiArg,
  openapi,
  PostMcbimItemsApiArg,
  PostMcbimItemsApiResponse,
} from "../../openapi";
import { getCacheKey } from "../apiRTK";
import { IFC_CLASSES_CEILING_DEVICES, IfcClassesEnum } from "../enums";
import { selectHttpMcbimSpacesResult } from "./mcbimSelectors";
import { selectActiveSingleRoomSummary } from "../Room/roomSelectors";
import { IRoom } from "@backend/src/schemas";

export interface IMcBimSliceState {
  test: string[];
}

const initialState: IMcBimSliceState = {
  test: [],
};

// If you are not using async thunks you can use the standalone `createSlice`.
export const McBimSlice = createAppSlice({
  name: "mcbim",
  initialState,
  reducers: () => ({}),
});

const selectFloorAndJob = (state: IRootState) => state.design.floorAndJob;
const selectProjectNo = (state: IRootState) => state.design.projectNo;
export const selectGetMcBimSpacesParams = createSelector(
  [selectFloorAndJob, selectProjectNo],
  (floorAndJob, projectNo): GetMcbimSpacesApiArg | null => {
    if (floorAndJob == null || floorAndJob.floorGlobalId == null) {
      return null;
    }
    return {
      projectNo: projectNo,
      floorGlobalId: floorAndJob.floorGlobalId as string,
      jobId: floorAndJob.jobId as string,
    };
  }
);

const selectFreeTextSearch = (state: IRootState) => state.design.freeTextSearch;
export const selectMcBimSpacesFromFreeTextSearch = createSelector(
  [selectFreeTextSearch, (state: IRootState, mcbimSpaces: PartialMcBimSpace[]) => mcbimSpaces],
  (freeTextSearch, mcbimSpaces) => {
    return getMcbimSpacesFromFreeTextSearch({
      freeTextSearch,
      mcbimSpaces,
      output: "MCBIMITEMS",
    });
  }
);

const selectedRoomNr = (state: IRootState) => state.design.selectedSpaceNumber;

export const selectMcBimSpaceFromSelectedRoomNr = createSelector(
  [selectedRoomNr, selectHttpMcbimSpacesResult],
  (selectedRoomNr, mcbimSpaces) => {
    return mcbimSpaces.find((space) => space.name === selectedRoomNr);
  }
);

export const selectArgsHttpMcBimSpaceStandardRoom = createSelector(
  [selectActiveSingleRoomSummary, selectFloorAndJob],
  (activeRoom, floorAndJob) => {
    if (!activeRoom || !floorAndJob) return {};
    return {
      projectNo: activeRoom.projectNo,
      floorGlobalId: floorAndJob.floorGlobalId,
      jobId: floorAndJob.jobId,
    };
  }
);

export const selectHttpMcBimItemsInActiveStandardRoom = createSelector(
  [selectArgsHttpMcBimSpaceStandardRoom, (state: IRootState) => state],
  (args, state) => {
    if (!args.projectNo || !args.floorGlobalId || !args.jobId) return [];
    const resp = openapi.endpoints.getMcbimItemsInSpace.select(args)(state).data;
    return resp ?? [];
  }
);

const selectIfcClassesToDisplayMap = (state: IRootState) => state.design.ifcClassesToDisplay;

// Plockar ut alla IFC-klasser som har value === true
const selectActiveInstanceClasses = createSelector(
  [selectIfcClassesToDisplayMap],
  (instanceClassesMap): IfcClassesEnum[] => {
    if (!instanceClassesMap) return [];

    return (Object.keys(instanceClassesMap) as IfcClassesEnum[]).filter(
      (key) => instanceClassesMap[key]
    );
  }
);

/** Hämtar argumenten som ska användas för att hämta McBimItems, eller null om vi inte ska hämta */
export const selectGetMcBimItemsArkArgs = createSelector(
  [selectActiveInstanceClasses, selectProjectNo, selectFloorAndJob],
  (activeInstanceClasses, projectNo, fileFloorJob): PostMcbimItemsApiArg | null => {
    if (!projectNo || !fileFloorJob?.floorName) return null;
    if (activeInstanceClasses.length === 0) return null; // inget att hämta på

    return {
      body: {
        projectNo,
        buildingStoryName: fileFloorJob.floorName,
        instanceClasses: activeInstanceClasses,
      },
    };
  }
);

const selectDeviceViewMode = (state: IRootState) => state.design.deviceViewMode;
export const selectGetMcBimCeilingDevicesArgs = createSelector(
  [selectProjectNo, selectFloorAndJob, selectDeviceViewMode],
  (projectNo, fileFloorJob, deviceViewMode): PostMcbimItemsApiArg | null => {
    if (!projectNo || !fileFloorJob?.floorName || deviceViewMode === "devices") return null;
    return {
      body: {
        projectNo,
        buildingStoryName: fileFloorJob.floorName,
        instanceClasses: [...IFC_CLASSES_CEILING_DEVICES],
      },
    };
  }
);

const enhancedApi = openapi.enhanceEndpoints({
  endpoints: {
    getMcbimSpaces: {
      providesTags: ["mcbim"],
    },
    postMultiplacerDevices: {
      providesTags: ["devices"],
    },
    postMultiplacerDeviceDeleteInRooms: {
      invalidatesTags: ["devices"],
    },
    getMultiplacerProjects: {
      providesTags: ["projects"],
    },
    getMultiplacerProjectsSingleProject: {
      providesTags: ["projects"],
    },
    postMultiplacerProjectEditProject: {
      invalidatesTags: ["projects"],
    },
    putMultiplacerRoomLock: {
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        await patchRoomFromQueryFulfilled({ dispatch, getState, queryFulfilled });
      },
    },
    // postMultiplacerRoomStandardRoomValidationApproval: {
    //   async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
    //     await patchRoomFromQueryFulfilled({ dispatch, getState, queryFulfilled });
    //   },
    // },
    postMultiplacerDeviceAddDevices: {
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        const addedDevices = (await queryFulfilled).data;

        const spaceValidationRun = (getState() as IRootState).design
          .spaceValidationRun as ISpaceValidationRun;
        const queries = getState().api.queries;
        const cacheKey = getCacheKey(queries, "postMultiplacerDevices");

        const patchResult = dispatch(
          openapi.util.updateQueryData("postMultiplacerDevices", cacheKey, (draft) => {
            draft.push(...addedDevices);
          })
        );

        if (patchResult.patches.length === 0) {
          alert("No patches in response");
          console.warn("No patches in response");
          return;
        }

        try {
          await queryFulfilled;
          if (spaceValidationRun.active) {
            dispatch(thunkRunSpaceValidations());
          }
          dispatch(thunkToast({ text: "Data er lagret", header: "Suksess", isSuccess: true }));
        } catch (error) {
          alert("Error updating device");
          console.warn("Error updating device", error);
          patchResult.undo();
        }
      },
    },
    putMultiplacerDeviceUpdate: {
      async onQueryStarted(resp, { dispatch, queryFulfilled, getState }) {
        const updatedDevice = resp.body.device;
        const spaceValidationRun = (getState() as IRootState).design
          .spaceValidationRun as ISpaceValidationRun;
        const queries = getState().api.queries;
        const cacheKey = getCacheKey(queries, "postMultiplacerDevices");
        const patchResult = dispatch(
          openapi.util.updateQueryData("postMultiplacerDevices", cacheKey, (draft) => {
            draft.map((_device) => {
              if (_device._id === updatedDevice._id) {
                Object.assign(_device, updatedDevice);
              }
              return _device;
            });
          })
        );

        try {
          await queryFulfilled;
          if (spaceValidationRun.active) {
            dispatch(thunkRunSpaceValidations());
          }
          dispatch(thunkToast({ text: "Data er lagret", header: "Suksess", isSuccess: true }));
        } catch (error) {
          console.warn("Error updating device", error);
          patchResult.undo();
        }
      },
    },
    deleteMultiplacerDeviceDeleteDevice: {
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        const spaceValidationRun = (getState() as IRootState).design
          .spaceValidationRun as ISpaceValidationRun;
        const queries = getState().api.queries;
        const cacheKey = getCacheKey(queries, "postMultiplacerDevices");
        const patchResult = dispatch(
          openapi.util.updateQueryData("postMultiplacerDevices", cacheKey, (draft) => {
            const index = draft.findIndex((device) => device._id === args.deviceId);
            if (index !== -1) {
              draft.splice(index, 1);
            }
          })
        );

        try {
          await queryFulfilled;
          if (spaceValidationRun.active) {
            dispatch(thunkRunSpaceValidations());
          }
          dispatch(thunkToast({ text: "Data er lagret", header: "Suksess", isSuccess: true }));
        } catch (error) {
          console.warn("Error updating device", error);
          patchResult.undo();
        }
      },
    },
    postMultiplacerRooms: {
      providesTags: ["Rooms"],
    },
    postMultiplacerAirflowsImport: {
      invalidatesTags: ["Rooms"],
    },
  },
});

// Typa ett enskilt objekt från GetMcbimSpacesApiResponse och lägg till searchHit och fillColor
export type PartialMcBimItem = (PostMcbimItemsApiResponse extends Array<infer T> ? T : never) & {
  searchHit?: boolean;
  fillColor?: string;
};

type PatchRoomArgs = {
  dispatch: AppDispatch;
  getState: () => IRootState;
  updatedRoom: IRoom;
};

export const patchRoomInCachedQueries = ({ dispatch, getState, updatedRoom }: PatchRoomArgs) => {
  if (!updatedRoom) return;

  const roomNumber = updatedRoom.roomNumber;
  const projectNo = updatedRoom.projectNo;

  const apiState: any = (getState() as IRootState).api;

  Object.values(apiState.queries).forEach((q: any) => {
    if (!q?.endpointName) return;

    // 1) Single room – match på projectNo + roomNumber
    if (
      q.endpointName === "getMultiplacerSingleRoom" &&
      q.originalArgs?.projectNo === projectNo &&
      q.originalArgs?.roomNumber === roomNumber
    ) {
      dispatch(
        openapi.util.updateQueryData("getMultiplacerSingleRoom", q.originalArgs, (draft: any) => {
          Object.assign(draft, updatedRoom); // mutera istället för return
        })
      );
    }

    // 2) Rooms-collection
    if (q.endpointName === "postMultiplacerRooms") {
      dispatch(
        openapi.util.updateQueryData("postMultiplacerRooms", q.originalArgs, (draft: any) => {
          if (draft?.[roomNumber]) {
            draft[roomNumber] = updatedRoom;
          }
        })
      );
    }
  });
};

// 🔹 Ny helper: tar queryFulfilled direkt
type PatchFromQueryFulfilledArgs = {
  dispatch: AppDispatch;
  getState: () => any;
  queryFulfilled: Promise<{ data: IRoom }>;
};

export const patchRoomFromQueryFulfilled = async ({
  dispatch,
  getState,
  queryFulfilled,
}: PatchFromQueryFulfilledArgs) => {
  try {
    const { data: updatedRoom } = await queryFulfilled;
    patchRoomInCachedQueries({ dispatch, getState, updatedRoom });
  } catch {
    // la RTK Query håndtere feilen som vanlig
  }
};
